import React from 'react';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';
import { PlainButton } from 'ui/public/button';
import BaseStyles from './change_password.module.css';
import Messages from './change_password_messages';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ChangePasswordStore } from './change_password_store';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { FormSubmitEvent } from 'ui/base/types';
import { useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { observer } from 'mobx-react';

type ChangePasswordStyles = {
  save: string;
  headerContainer: string;
  formContainer: string;
};

const themedStyles: ThemedStyles<ChangePasswordStyles> = {
  [Theme.DAYLIGHT]: {
    save: BaseStyles.daylightSave,
    headerContainer: BaseStyles.headerContainer,
    formContainer: BaseStyles.formContainer,
  },
  [Theme.MIDNIGHT]: {
    save: BaseStyles.midnightSave,
    headerContainer: BaseStyles.headerContainer,
    formContainer: BaseStyles.formContainer,
  },
};

type BaseChangePasswordProps = {
  store: ChangePasswordStore;
};

const BaseChangePassword = observer(({ store }: BaseChangePasswordProps) => {
  const accounts = useAccountsDomain();
  const history = useHistory();
  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      history.goBack();
    }
  }, [accounts, history, store]);

  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <form onSubmit={handleSave}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            className={styles.save}>{Messages.save()}</PlainButton>
        </div>
        <div className={styles.formContainer}>
          <LabeledInput label={Messages.oldPassword()}>
            <PasswordInput store={store.oldPassword}/>
          </LabeledInput>
          <ErrorMessage message={store.oldPasswordError}/>
          <LabeledInput label={Messages.newPassword()}>
            <PasswordInput store={store.newPassword}/>
          </LabeledInput>
          <ErrorMessage message={store.newPasswordError}/>
          <LabeledInput label={Messages.confirmPassword()}>
            <PasswordInput store={store.confirmPassword}/>
          </LabeledInput>
          <ErrorMessage message={store.confirmPasswordError}/>
        </div>
      </form>
    </StandardPage>
  );
});

export const ChangePassword = () => {
  const [store] = React.useState(new ChangePasswordStore());
  return <BaseChangePassword store={store}/>;
};
